import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { studyNotesService, slidesService, sessionsService, commentsService, reactionsService } from '../../services/studyNotesService';
import { bibleService } from '../../services/bibleService';
import Icon from '../../components/AppIcon';
import { QRCodeSVG } from 'qrcode.react';

const THEME_CLASSES = {
  light: { bg: 'bg-gray-50', card: 'bg-white', text: 'text-gray-900', sub: 'text-gray-500', border: 'border-gray-200' },
  dark: { bg: 'bg-gray-900', card: 'bg-gray-800', text: 'text-white', sub: 'text-gray-400', border: 'border-gray-700' },
  'high-contrast': { bg: 'bg-black', card: 'bg-gray-950', text: 'text-yellow-300', sub: 'text-yellow-500', border: 'border-yellow-700' },
};

const FONT_SIZE_CLASSES = {
  small:  { title: 'text-xl',  body: 'text-sm',  ref: 'text-xs'  },
  medium: { title: 'text-3xl', body: 'text-lg',  ref: 'text-base' },
  large:  { title: 'text-4xl', body: 'text-xl',  ref: 'text-lg'  },
};

// Extended step-based font sizes for audience (synced with presenter steps 0-8)
const FONT_SIZE_STEPS_AUDIENCE = [
  { title: 'text-xl',  body: 'text-sm',  ref: 'text-xs'  },
  { title: 'text-2xl', body: 'text-base',ref: 'text-xs'  },
  { title: 'text-3xl', body: 'text-lg',  ref: 'text-base'},
  { title: 'text-4xl', body: 'text-xl',  ref: 'text-lg'  },
  { title: 'text-5xl', body: 'text-2xl', ref: 'text-xl'  },
  { title: 'text-6xl', body: 'text-3xl', ref: 'text-2xl' },
  { title: 'text-7xl', body: 'text-4xl', ref: 'text-3xl' },
  { title: 'text-8xl', body: 'text-5xl', ref: 'text-4xl' },
  { title: 'text-9xl', body: 'text-6xl', ref: 'text-5xl' },
];

const REACTIONS = [
  { type: 'thumbs_up', emoji: '👍', label: 'Amen' },
  { type: 'pray', emoji: '🙏', label: 'Praying' },
  { type: 'cross', emoji: '✝️', label: 'Praise' },
];

const getFingerprint = () => {
  let fp = localStorage.getItem('_fm_fp');
  if (!fp) {
    fp = Math.random()?.toString(36)?.slice(2) + Date.now()?.toString(36);
    localStorage.setItem('_fm_fp', fp);
  }
  return fp;
};

// Parse a verse reference like "John 3:16" or "Romans 8:1-5" into parts
const parseVerseRef = (ref) => {
  if (!ref) return null;
  const match = ref?.trim()?.match(/^(.+?)\s+(\d+):(\d+)(?:-(\d+))?$/);
  if (!match) return null;
  return {
    book: match?.[1],
    chapter: parseInt(match?.[2]),
    verseStart: parseInt(match?.[3]),
    verseEnd: match?.[4] ? parseInt(match?.[4]) : parseInt(match?.[3]),
  };
};

// Share Modal Component
const ShareModal = ({ note, onClose, tc }) => {
  const shareUrl = window.location?.href;
  const [copied, setCopied] = useState(false);
  const [shareTab, setShareTab] = useState('qr'); // 'qr' | 'link'

  const handleNativeShare = async () => {
    if (navigator?.share) {
      try {
        await navigator.share({
          title: note?.title || 'Study Notes',
          text: note?.speaker ? `${note?.title} — ${note?.speaker}` : note?.title,
          url: shareUrl,
        });
      } catch (err) {
        if (err?.name !== 'AbortError') console.error(err);
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard?.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error(err);
    }
  };

  const hasNativeShare = !!navigator?.share;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className={`${tc?.card} rounded-2xl w-full max-w-sm shadow-2xl`}
        onClick={e => e?.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${tc?.border}`}>
          <h3 className={`text-base font-semibold ${tc?.text}`}>Share Presentation</h3>
          <button onClick={onClose} className={`p-1.5 hover:bg-gray-100 rounded-lg transition-colors`}>
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Note info */}
        <div className={`px-4 pt-4 pb-2`}>
          <p className={`text-sm font-medium ${tc?.text} truncate`}>{note?.title}</p>
          {note?.speaker && <p className={`text-xs ${tc?.sub}`}>{note?.speaker}</p>}
        </div>

        {/* Tabs */}
        <div className={`flex mx-4 mb-4 rounded-xl overflow-hidden border ${tc?.border}`}>
          <button
            onClick={() => setShareTab('qr')}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${
              shareTab === 'qr' ? 'bg-blue-500 text-white' : `${tc?.card} ${tc?.text}`
            }`}
          >
            QR Code
          </button>
          <button
            onClick={() => setShareTab('link')}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${
              shareTab === 'link' ? 'bg-blue-500 text-white' : `${tc?.card} ${tc?.text}`
            }`}
          >
            Copy Link
          </button>
        </div>

        {/* QR Code Tab */}
        {shareTab === 'qr' && (
          <div className="flex flex-col items-center px-4 pb-4">
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-3">
              <QRCodeSVG
                value={shareUrl}
                size={180}
                level="M"
                includeMargin={false}
                fgColor="#1e293b"
                bgColor="#ffffff"
              />
            </div>
            <p className={`text-xs ${tc?.sub} text-center mb-4`}>
              Scan to follow along on your device
            </p>
            {hasNativeShare && (
              <button
                onClick={handleNativeShare}
                className="w-full flex items-center justify-center gap-2 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium transition-colors"
              >
                <Icon name="Share2" size={16} />
                Share via...
              </button>
            )}
          </div>
        )}

        {/* Link Tab */}
        {shareTab === 'link' && (
          <div className="px-4 pb-4">
            <div className={`flex items-center gap-2 p-3 rounded-xl border ${tc?.border} ${tc?.bg} mb-3`}>
              <p className={`flex-1 text-xs ${tc?.sub} truncate font-mono`}>{shareUrl}</p>
            </div>
            <button
              onClick={handleCopyLink}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-colors ${
                copied
                  ? 'bg-green-500 text-white' :'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              <Icon name={copied ? 'Check' : 'Copy'} size={16} />
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
            {hasNativeShare && (
              <button
                onClick={handleNativeShare}
                className={`w-full flex items-center justify-center gap-2 py-3 mt-2 rounded-xl text-sm font-medium border ${tc?.border} ${tc?.text} transition-colors hover:bg-gray-50`}
              >
                <Icon name="Share2" size={16} />
                Share via...
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AudienceView = () => {
  const { slug } = useParams();
  const { user, userProfile } = useAuth();

  const [note, setNote] = useState(null);
  const [slides, setSlides] = useState([]);
  const [session, setSession] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState('KJV');
  const [expandedSlides, setExpandedSlides] = useState({});
  const [reactions, setReactions] = useState({ thumbs_up: 0, pray: 0, cross: 0 });
  const [reactionAnim, setReactionAnim] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [joinCodeVerified, setJoinCodeVerified] = useState(false);
  const [joinCodeError, setJoinCodeError] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [theme, setTheme] = useState('light');
  const [fontSize, setFontSize] = useState('medium');
  // Cache of fetched verse texts: key = "ref|version" -> text
  const [verseCache, setVerseCache] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);

  const sessionChannelRef = useRef(null);
  const commentChannelRef = useRef(null);
  const reactionChannelRef = useRef(null);
  const commentsEndRef = useRef(null);
  const noteRef = useRef(null);

  useEffect(() => {
    loadData();
    const savedVersion = localStorage.getItem('_fm_audience_version');
    if (savedVersion) setVersion(savedVersion);
    return () => {
      sessionChannelRef?.current?.unsubscribe();
      commentChannelRef?.current?.unsubscribe();
      reactionChannelRef?.current?.unsubscribe();
    };
  }, [slug]);

  useEffect(() => {
    commentsEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  // Pre-fetch verse text when slide or version changes
  useEffect(() => {
    if (!slides?.length || !noteRef?.current) return;
    const toFetch = [currentIndex, currentIndex + 1]?.filter(i => i >= 0 && i < slides?.length);
    toFetch?.forEach(i => {
      const slide = slides?.[i];
      if (slide?.slideType === 'verse' && slide?.verseReference) {
        const ver = slide?.versionOverride || version;
        const cacheKey = `${slide?.verseReference}|${ver}`;
        if (!verseCache?.[cacheKey]) {
          fetchVerseText(slide?.verseReference, ver, cacheKey);
        }
      }
    });
  }, [currentIndex, slides, version]);

  const fetchVerseText = async (ref, ver, cacheKey) => {
    const parsed = parseVerseRef(ref);
    if (!parsed) return;
    try {
      const { data } = await bibleService?.getChapterVerses(parsed?.book, parsed?.chapter, ver);
      if (data && data?.length > 0) {
        const verses = data?.filter(v => v?.verse_number >= parsed?.verseStart && v?.verse_number <= parsed?.verseEnd);
        const text = verses?.map(v => v?.text)?.join(' ');
        if (text) {
          setVerseCache(prev => ({ ...prev, [cacheKey]: text }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch verse:', err);
    }
  };

  const getVerseText = (slide) => {
    if (!slide?.verseReference) return null;
    const ver = slide?.versionOverride || version;
    const cacheKey = `${slide?.verseReference}|${ver}`;
    return verseCache?.[cacheKey] || null;
  };

  // Render verse text with KJV markup:
  // <text> = red (words of Christ)
  // [text] = italic
  const renderVerseText = (text) => {
    if (!text) return null;
    const parts = [];
    let i = 0;
    let key = 0;
    while (i < text?.length) {
      if (text?.[i] === '<') {
        const end = text?.indexOf('>', i);
        if (end !== -1) {
          parts?.push(
            <span key={key++} className="text-red-500 font-medium">
              {text?.slice(i + 1, end)}
            </span>
          );
          i = end + 1;
          continue;
        }
      }
      if (text?.[i] === '[') {
        const end = text?.indexOf(']', i);
        if (end !== -1) {
          parts?.push(
            <em key={key++} className="italic opacity-80">
              {text?.slice(i + 1, end)}
            </em>
          );
          i = end + 1;
          continue;
        }
      }
      let j = i + 1;
      while (j < text?.length && text?.[j] !== '<' && text?.[j] !== '[') j++;
      parts?.push(<span key={key++}>{text?.slice(i, j)}</span>);
      i = j;
    }
    return parts;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const noteData = await studyNotesService?.getNoteBySlug(slug);
      if (!noteData) { setError('This study note was not found.'); return; }

      noteRef.current = noteData;
      setNote(noteData);
      setTheme(noteData?.theme || 'light');
      setFontSize(noteData?.font_size || 'medium');
      const savedVer = localStorage.getItem('_fm_audience_version') || noteData?.default_version || 'KJV';
      setVersion(savedVer);

      const slideData = await slidesService?.getSlides(noteData?.id);
      setSlides(slideData);

      const sessionData = await sessionsService?.getOrCreateSession(noteData?.id);
      setSession(sessionData);
      setCurrentIndex(sessionData?.current_slide_index || 0);

      // Load reactions
      const reactionCounts = await reactionsService?.getReactionCounts(sessionData?.id);
      setReactions(reactionCounts);

      // Load comments
      const commentData = await commentsService?.getComments(sessionData?.id);
      setComments(commentData);

      // Subscribe to session (for live sync) — uses broadcast + postgres_changes
      if (noteData?.sync_mode === 'live') {
        sessionChannelRef.current = sessionsService?.subscribeToSession(sessionData?.id, (updated) => {
          if (updated?.current_slide_index !== undefined) {
            setCurrentIndex(updated?.current_slide_index);
          }
          setSession(prev => ({ ...prev, ...updated }));
        });
      }

      // Subscribe to comments
      commentChannelRef.current = commentsService?.subscribeToComments(sessionData?.id, (newComment) => {
        if (!newComment?.is_deleted && newComment?.is_approved) {
          setComments(prev => [...prev, newComment]);
        }
      });

      // Subscribe to reactions
      reactionChannelRef.current = reactionsService?.subscribeToReactions(sessionData?.id, (newReaction) => {
        setReactions(prev => ({
          ...prev,
          [newReaction?.reaction_type]: (prev?.[newReaction?.reaction_type] || 0) + 1,
        }));
      });
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionChange = (v) => {
    setVersion(v);
    localStorage.setItem('_fm_audience_version', v);
  };

  const toggleExpand = (index) => {
    setExpandedSlides(prev => ({ ...prev, [index]: !prev?.[index] }));
  };

  const handleReaction = async (type) => {
    if (!session) return;
    try {
      setReactionAnim(type);
      setTimeout(() => setReactionAnim(null), 600);
      await reactionsService?.addReaction({
        noteId: note?.id,
        sessionId: session?.id,
        reactionType: type,
        fingerprint: getFingerprint(),
      });
    } catch (err) {
      console.error(err);
    }
  };

  const verifyJoinCode = () => {
    if (joinCode === session?.qa_join_code) {
      setJoinCodeVerified(true);
      setJoinCodeError('');
    } else {
      setJoinCodeError('Incorrect code. Please try again.');
    }
  };

  const submitComment = async () => {
    if (!commentText?.trim()) return;
    try {
      setSubmittingComment(true);
      await commentsService?.postComment({
        noteId: note?.id,
        sessionId: session?.id,
        slideIndex: currentIndex,
        userId: user?.id || null,
        displayName: user ? (userProfile?.full_name || userProfile?.username || user?.email?.split('@')?.[0]) : (displayName || 'Anonymous'),
        content: commentText?.trim(),
      });
      setCommentText('');
    } catch (err) {
      console.error(err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const currentSlide = slides?.[currentIndex];
  const tc = THEME_CLASSES?.[theme] || THEME_CLASSES?.light;
  const fs = FONT_SIZE_CLASSES?.[fontSize] || FONT_SIZE_CLASSES?.medium;
  const isSelfPaced = note?.sync_mode === 'self-paced';

  const getSlideText = (slide) => {
    if (!slide) return '';
    return slide?.textContent || '';
  };

  const shouldTruncate = (text) => text && text?.length > 250;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="FileX" size={28} className="text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Note Not Found</h2>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col ${tc?.bg}`}>
      {/* Header */}
      <div className={`${tc?.card} border-b ${tc?.border} px-4 py-3`}>
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h1 className={`text-sm font-semibold ${tc?.text} truncate`}>{note?.title}</h1>
            {note?.speaker && <p className={`text-xs ${tc?.sub}`}>{note?.speaker}</p>}
          </div>
          <div className="flex items-center gap-2 ml-3">
            {/* Version selector */}
            <select
              value={version}
              onChange={e => handleVersionChange(e?.target?.value)}
              className={`px-2 py-1 text-xs rounded-lg border ${tc?.border} ${tc?.card} ${tc?.text} focus:outline-none`}
            >
              {['KJV', 'NKJV', 'NIV', 'ESV', 'NLT', 'NASB', 'CSB', 'AMP', 'MSG']?.map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
            {/* Theme toggle */}
            <select
              value={theme}
              onChange={e => setTheme(e?.target?.value)}
              className={`px-2 py-1 text-xs rounded-lg border ${tc?.border} ${tc?.card} ${tc?.text} focus:outline-none`}
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="high-contrast">High Contrast</option>
            </select>
            {/* Share button */}
            <button
              onClick={() => setShowShareModal(true)}
              className={`p-1.5 rounded-lg border ${tc?.border} ${tc?.card} hover:bg-blue-50 transition-colors`}
              title="Share this presentation"
            >
              <Icon name="Share2" size={16} color="#3b82f6" />
            </button>
          </div>
        </div>
      </div>
      {/* Slide Display */}
      <div className="flex-1 max-w-2xl mx-auto w-full px-4 py-8">
        {currentSlide ? (
          <div className={`${tc?.card} rounded-2xl shadow-sm border ${tc?.border} p-8 mb-6`}>
            {currentSlide?.slideType === 'title' && (
              <div className="text-center">
                {note?.church_name && (
                  <p className={`text-xs uppercase tracking-widest ${tc?.sub} mb-4`}>{note?.church_name}</p>
                )}
                <h1 className={`${fs?.title} font-bold ${tc?.text} mb-4`}>
                  {currentSlide?.textContent || note?.title}
                </h1>
                {note?.speaker && <p className={`${fs?.body} ${tc?.sub}`}>{note?.speaker}</p>}
              </div>
            )}

            {currentSlide?.slideType === 'verse' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`${fs?.ref} font-bold ${tc?.text}`}>{currentSlide?.verseReference}</h2>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${tc?.border} ${tc?.sub}`}>
                    {currentSlide?.versionOverride || version}
                  </span>
                </div>
                <div className={`${fs?.body} ${tc?.text} leading-relaxed`}>
                  {getVerseText(currentSlide) ? (
                    (() => {
                      const text = getVerseText(currentSlide);
                      const truncated = shouldTruncate(text) && !expandedSlides?.[currentIndex];
                      return (
                        <div>
                          {renderVerseText(text)}
                          {shouldTruncate(text) && (
                            <button
                              onClick={() => toggleExpand(currentIndex)}
                              className="mt-2 text-xs text-blue-500 hover:underline"
                            >
                              {truncated ? 'Show more' : 'Show less'}
                            </button>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <p className="italic opacity-50">Loading verse…</p>
                  )}
                </div>
                <a
                  href={`/bible-reader?ref=${encodeURIComponent(currentSlide?.verseReference)}`}
                  className="inline-flex items-center gap-1.5 mt-4 text-xs text-blue-500 hover:underline"
                >
                  <Icon name="ExternalLink" size={12} />
                  View in Reader
                </a>
              </div>
            )}

            {currentSlide?.slideType === 'text' && (
              <div>
                {(() => {
                  const text = getSlideText(currentSlide);
                  const truncated = shouldTruncate(text) && !expandedSlides?.[currentIndex];
                  return (
                    <div>
                      <p className={`${fs?.body} ${tc?.text} leading-relaxed`}>
                        {truncated ? text?.slice(0, 250) + '…' : text}
                      </p>
                      {shouldTruncate(text) && (
                        <button
                          onClick={() => toggleExpand(currentIndex)}
                          className="mt-2 text-xs text-blue-500 hover:underline"
                        >
                          {truncated ? 'Show more' : 'Show less'}
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        ) : (
          <div className={`${tc?.card} rounded-2xl p-12 text-center border ${tc?.border}`}>
            <p className={tc?.sub}>Waiting for presenter...</p>
          </div>
        )}

        {/* Navigation (self-paced only) */}
        {isSelfPaced && slides?.length > 1 && (
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
              disabled={currentIndex === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${tc?.card} border ${tc?.border} ${tc?.text} disabled:opacity-30 transition-colors`}
            >
              <Icon name="ChevronLeft" size={16} />
              Previous
            </button>
            <span className={`text-sm ${tc?.sub}`}>{currentIndex + 1} / {slides?.length}</span>
            <button
              onClick={() => setCurrentIndex(i => Math.min(slides?.length - 1, i + 1))}
              disabled={currentIndex === slides?.length - 1}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${tc?.card} border ${tc?.border} ${tc?.text} disabled:opacity-30 transition-colors`}
            >
              Next
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        )}

        {/* Slide dots */}
        {slides?.length > 1 && (
          <div className="flex justify-center gap-1.5 mb-6">
            {slides?.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === currentIndex ? 'w-6 bg-blue-500' : `w-1.5 ${tc?.sub} opacity-40 bg-current`
                }`}
              />
            ))}
          </div>
        )}

        {/* Reaction Bar */}
        <div className={`${tc?.card} rounded-2xl border ${tc?.border} p-4 mb-6`}>
          <p className={`text-xs ${tc?.sub} text-center mb-3`}>React to this message</p>
          <div className="flex items-center justify-center gap-4">
            {REACTIONS?.map(r => (
              <button
                key={r?.type}
                onClick={() => handleReaction(r?.type)}
                className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all ${
                  reactionAnim === r?.type ? 'scale-125' : 'hover:scale-110'
                } ${tc?.card} border ${tc?.border}`}
              >
                <span className="text-2xl">{r?.emoji}</span>
                <span className={`text-xs ${tc?.sub}`}>{reactions?.[r?.type] || 0}</span>
                <span className={`text-xs ${tc?.sub}`}>{r?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Q&A Section */}
        {session?.qa_enabled ? (
          <div className={`${tc?.card} rounded-2xl border ${tc?.border} p-4`}>
            <h3 className={`text-sm font-semibold ${tc?.text} mb-4 flex items-center gap-2`}>
              <Icon name="MessageSquare" size={16} />
              Q&A
              <span className={`text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700`}>Open</span>
            </h3>

            {/* Comments list */}
            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
              {comments?.length === 0 ? (
                <p className={`text-xs ${tc?.sub} text-center py-4`}>No questions yet. Be the first!</p>
              ) : (
                comments?.map(c => (
                  <div key={c?.id} className={`p-3 rounded-lg border ${tc?.border} bg-opacity-50`}>
                    <p className={`text-xs font-medium ${tc?.text} mb-1`}>{c?.display_name || 'Anonymous'}</p>
                    <p className={`text-sm ${tc?.text}`}>{c?.content}</p>
                  </div>
                ))
              )}
              <div ref={commentsEndRef} />
            </div>

            {/* Join code gate */}
            {!joinCodeVerified && !user ? (
              <div className={`p-4 rounded-xl border ${tc?.border} bg-opacity-50`}>
                <p className={`text-xs ${tc?.sub} mb-3`}>Enter the join code from your presenter to ask questions:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={joinCode}
                    onChange={e => setJoinCode(e?.target?.value)}
                    onKeyDown={e => e?.key === 'Enter' && verifyJoinCode()}
                    placeholder="Enter code"
                    maxLength={6}
                    className={`flex-1 px-3 py-2 rounded-lg border ${tc?.border} ${tc?.card} ${tc?.text} text-sm focus:outline-none text-center tracking-widest font-mono`}
                  />
                  <button
                    onClick={verifyJoinCode}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors"
                  >
                    Join
                  </button>
                </div>
                {joinCodeError && <p className="text-xs text-red-500 mt-2">{joinCodeError}</p>}
              </div>
            ) : (
              <div className="space-y-2">
                {!user && (
                  <input
                    type="text"
                    value={displayName}
                    onChange={e => setDisplayName(e?.target?.value)}
                    placeholder="Your name (optional)"
                    className={`w-full px-3 py-2 rounded-lg border ${tc?.border} ${tc?.card} ${tc?.text} text-sm focus:outline-none`}
                  />
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={commentText}
                    onChange={e => setCommentText(e?.target?.value)}
                    onKeyDown={e => e?.key === 'Enter' && !e?.shiftKey && submitComment()}
                    placeholder="Ask a question..."
                    maxLength={500}
                    className={`flex-1 px-3 py-2 rounded-lg border ${tc?.border} ${tc?.card} ${tc?.text} text-sm focus:outline-none`}
                  />
                  <button
                    onClick={submitComment}
                    disabled={submittingComment || !commentText?.trim()}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  >
                    {submittingComment ? '...' : 'Send'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={`${tc?.card} rounded-2xl border ${tc?.border} p-4 text-center`}>
            <Icon name="MessageSquareOff" size={20} className={`mx-auto mb-2 ${tc?.sub}`} />
            <p className={`text-sm ${tc?.sub}`}>Q&A is currently closed</p>
          </div>
        )}
      </div>
      {/* Footer */}
      <div className={`${tc?.card} border-t ${tc?.border} py-3 px-4 text-center`}>
        <p className={`text-xs ${tc?.sub}`}>
          Powered by <a href="/" className="text-blue-500 hover:underline">Fortified Mind</a>
        </p>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          note={note}
          onClose={() => setShowShareModal(false)}
          tc={tc}
        />
      )}
    </div>
  );
};

export default AudienceView;
